import { useState, useEffect } from 'react';
import styled from 'styled-components';
import { db } from '../../../firebase/config';
import { collection, addDoc, getDocs, deleteDoc, doc, query, orderBy, serverTimestamp } from 'firebase/firestore';
import Pagination from '../../../components/common/Pagination';

const PER_PAGE = 10;

const AppUpdate = () => {
  const [version, setVersion] = useState('');
  const [content, setContent] = useState('');
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [page, setPage] = useState(1);

  const loadUpdates = async () => {
    setListLoading(true);
    try {
      const q = query(collection(db, 'app_updates'), orderBy('createdAt', 'desc'));
      const snap = await getDocs(q);
      setUpdates(snap.docs.map((d) => {
        const data = d.data();
        let date = '';
        if (data.createdAt?.toDate) date = data.createdAt.toDate().toLocaleString('ko-KR');
        else if (data.createdAt?.seconds) date = new Date(data.createdAt.seconds * 1000).toLocaleString('ko-KR');
        return { id: d.id, ...data, date };
      }));
    } finally {
      setListLoading(false);
    }
  };

  useEffect(() => { loadUpdates(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!version.trim()) { alert('버전을 입력하세요.'); return; }
    setLoading(true);
    try {
      await addDoc(collection(db, 'app_updates'), {
        version: version.trim(),
        content: content.trim(),
        createdAt: serverTimestamp(),
      });
      setVersion('');
      setContent('');
      await loadUpdates();
      alert('앱 업데이트가 등록되었습니다. 전체 유저에게 푸시가 발송됩니다.');
    } catch (err) {
      alert('등록 실패: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('삭제하시겠습니까?')) return;
    await deleteDoc(doc(db, 'app_updates', id));
    await loadUpdates();
  };

  const paged = updates.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  return (
    <Wrap>
      <Title>앱 업데이트 관리</Title>
      <Desc>새 버전을 등록하면 전체 유저에게 푸시 알림이 발송되고, 앱 접속 시 자동 새로고침됩니다.</Desc>

      <Form onSubmit={handleSubmit}>
        <Row>
          <Field>
            <Label>버전</Label>
            <Input placeholder="예: 1.0.1" value={version} onChange={(e) => setVersion(e.target.value)} />
          </Field>
          <Field style={{ flex: 2 }}>
            <Label>업데이트 내용</Label>
            <Input placeholder="예: 버그 수정 및 성능 개선" value={content} onChange={(e) => setContent(e.target.value)} />
          </Field>
          <SubmitBtn type="submit" disabled={loading}>{loading ? '등록중...' : '등록'}</SubmitBtn>
        </Row>
      </Form>

      <Table>
        <thead>
          <tr>
            <Th>버전</Th>
            <Th>내용</Th>
            <Th>등록일시</Th>
            <Th>관리</Th>
          </tr>
        </thead>
        <tbody>
          {paged.map((u) => (
            <tr key={u.id}>
              <Td>{u.version}</Td>
              <Td>{u.content || '-'}</Td>
              <Td>{u.date}</Td>
              <Td><DelBtn onClick={() => handleDelete(u.id)}>삭제</DelBtn></Td>
            </tr>
          ))}
          {listLoading ? (
            <tr><Td colSpan={4} style={{ textAlign: 'center', padding: '40px 0' }}>로딩 중...</Td></tr>
          ) : updates.length === 0 && (
            <tr><Td colSpan={4} style={{ textAlign: 'center', color: '#9CA3AF' }}>등록된 업데이트가 없습니다.</Td></tr>
          )}
        </tbody>
      </Table>

      <Pagination current={page} total={Math.ceil(updates.length / PER_PAGE)} onChange={setPage} />
    </Wrap>
  );
};

export default AppUpdate;

// ───── 스타일 ─────
const Wrap = styled.div``;
const Title = styled.h2`
  font-size: ${({ theme }) => theme.fontSize.xl};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;
const Desc = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-bottom: ${({ theme }) => theme.spacing.lg};
`;
const Form = styled.form`
  margin-bottom: ${({ theme }) => theme.spacing.xl};
`;
const Row = styled.div`
  display: flex;
  gap: ${({ theme }) => theme.spacing.md};
  align-items: flex-end;
`;
const Field = styled.div`
  flex: 1;
`;
const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;
const Input = styled.input`
  width: 100%;
  padding: 10px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.base};
  outline: none;
  &:focus { border-color: ${({ theme }) => theme.colors.primary}; }
`;
const SubmitBtn = styled.button`
  padding: 10px 24px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.sm};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  &:disabled { opacity: 0.6; }
`;
const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;
const Th = styled.th`
  text-align: left;
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.textSecondary};
  border-bottom: 2px solid ${({ theme }) => theme.colors.border};
`;
const Td = styled.td`
  padding: 10px 12px;
  font-size: ${({ theme }) => theme.fontSize.base};
  color: ${({ theme }) => theme.colors.text};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borderLight};
`;
const DelBtn = styled.button`
  padding: 4px 10px;
  background: none;
  border: 1px solid ${({ theme }) => theme.colors.danger};
  color: ${({ theme }) => theme.colors.danger};
  border-radius: 4px;
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
`;
